"use client";
import Link from "next/link";

import FaqSection from "@/components/FaqSection";
import { BreadcrumbSchema,ProductSchema } from "@/components/JsonLd";
import PageShell from "@/components/PageShell";

// Rewritten 2026-10-08. The previous page sold regulator monitoring, a filing
// calendar with "alerts 14 days before every deadline", change detection
// "within 24 hours" via "official API feeds", and "TLS 1.3". On this product
// the compliance alerts, compliance calendar and reg-change routers are all
// mounted `require_india_edition` (404 for a global org), and their plan keys
// are in INDIA_ONLY_FEATURE_KEYS. What a global organisation has is:
//   - Privacy & Data Protection (`/privacy-program`, `privacy_compliance`:
//     global_pro / global_business / global_team; fourteen law packs a global
//     org may hold, every one `content_status="drafted"`);
//   - the obligation register, whose reminder job does run;
//   - Audit readiness, where the only framework a global org sees is SOC 2.
const PRODUCT_FAQS = [
  {
    q: "What does Compliance AI cover on this product?",
    a: "Three things: a privacy programme under the data protection laws your organisation is subject to, an obligation register for what your contracts bind you to, and a SOC 2 readiness assessment judged against your own policies. It does not monitor regulators or keep a statutory filing calendar.",
  },
  {
    q: "Which privacy laws?",
    a: "Fourteen law packs: EU GDPR, UK GDPR, the California CCPA/CPRA, New York's SHIELD Act, the Delaware Personal Data Privacy Act, Canada's PIPEDA, Australia's Privacy Act, New Zealand's Privacy Act 2020, Singapore's PDPA, Hong Kong's PDPO, South Africa's POPIA, Kenya's Data Protection Act, Nigeria's NDPA and the DIFC Data Protection Law. Your organisation's country picks the default; an admin can hold several.",
  },
  {
    q: "Have the law packs been checked by a lawyer?",
    a: "Not yet. Every pack is drafted — its content has not been verified against the official text by counsel — and the product says so on screen. Use the module to run and evidence your programme, and have counsel confirm your position. It is not legal advice.",
  },
  {
    q: "Which plans include it?",
    a: "The privacy programme is on Pro, Business and Enterprise, not Starter. The obligation register and audit readiness are on every plan.",
  },
  {
    q: "Is my data secure?",
    a: "Data is encrypted at rest with AES-256 and in transit with TLS 1.2 or later. Where it is stored, and which sub-processors receive content, is set out on the Security page and in the DPA.",
  },
];

export default function ComplianceAIPage() {
  return (
    <>
      <ProductSchema slug="compliance-ai" />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Product", url: "/product" }, { name: "Compliance AI", url: "/product/compliance-ai" }]} />
      <PageShell>
        {/* Hero */}
        <section style={{padding:"72px clamp(20px,4vw,52px) 60px",background:"var(--o950)",color:"var(--b50)"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"52px",alignItems:"center"}}>
            <div>
              <div className="ey ey-d rev">02 / Compliance AI</div>
              <h1 className="rev d1" style={{fontFamily:"var(--font-fraunces), serif",fontSize:"clamp(36px,4.5vw,56px)",fontWeight:200,lineHeight:1.08,letterSpacing:"-.03em",marginBottom:"18px"}}>Run the programme.{" "}<br /><em style={{fontStyle:"italic",color:"var(--cl)"}}>Keep the evidence.</em></h1>
              <p className="rev d2" style={{fontSize:"16px",color:"rgba(251,250,246,.45)",lineHeight:1.8,maxWidth:"440px",marginBottom:"28px"}}>A privacy programme under the data protection laws you are subject to, the obligations your contracts create — tracked with evidence and reminders that fire — and SOC 2 readiness judged against your own policies.</p>
              <div className="rev d3" style={{display:"flex",gap:"10px"}}>
                <a className="cta-p" data-mag="true" href={`/sign-up`}><span className="cta-p-t">Get started →</span></a>
                <Link className="cta-s" href="/features/privacy-readiness">Privacy &amp; Data Protection</Link>
              </div>
            </div>
            <div style={{background:"var(--o800)",border:"1px solid rgba(251,250,246,.08)",borderRadius:"8px",padding:"24px"}}>
              <div style={{fontSize:"10px",color:"rgba(251,250,246,.25)",letterSpacing:".06em",marginBottom:"16px"}}>Illustration · Privacy programme</div>
              {[
                {reg:"Laws held",desc:"UK GDPR · EU GDPR — drafted, not yet counsel-reviewed",days:"2 packs",color:"#FFBD2E"},
                {reg:"Rights request",desc:"Access request — response period from the law that applies",days:"Open",color:"#28CA41"},
                {reg:"Incident",desc:"Breach assessment — notification clocks per law",days:"Assess",color:"#FF6B6B"},
                {reg:"Processor",desc:"DPA review against the law's required clauses",days:"Review",color:"#28CA41"},
              ].map((a,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:"12px",padding:"12px 14px",background:"rgba(251,250,246,.03)",border:"1px solid rgba(251,250,246,.06)",borderRadius:"4px",marginBottom:"6px"}}>
                  <div style={{width:"6px",height:"6px",borderRadius:"50%",background:a.color,flexShrink:0}}></div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:"11px",color:"rgba(251,250,246,.6)"}}><strong style={{color:"rgba(251,250,246,.8)"}}>{a.reg}</strong> — {a.desc}</div>
                  </div>
                  <div style={{fontSize:"10px",color:a.color,flexShrink:0}}>{a.days}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section style={{padding:"40px clamp(20px,4vw,52px)",borderBottom:"1px solid var(--b200)",background:"var(--b100)"}}>
          <div style={{maxWidth:"820px",background:"var(--b50)",border:"1px solid var(--b200)",borderRadius:"6px",padding:"26px 30px"}}>
            <div className="ey rev">Key facts</div>
            <ul style={{listStyle:"none",margin:0,padding:0,display:"flex",flexDirection:"column",gap:"10px"}}>
              {[
                <>Fourteen privacy law packs on one engine; your organisation&apos;s country picks the default and an admin can hold several. With no country set, it asks rather than guesses.</>,
                <>Every law pack is drafted — not yet verified against the official text by counsel — and the product says so on screen. It is not legal advice.</>,
                <>A status of compliant needs linked evidence and a person&apos;s approval; it is never computed from a score alone.</>,
                <>The <Link href="/features/obligations" style={{color:"var(--o900)",textDecoration:"underline",textUnderlineOffset:"3px"}}>obligation register</Link> pulls obligations out of your contracts and reminds you on each one&apos;s own lead times.</>,
                <><Link href="/features/audit-readiness" style={{color:"var(--o900)",textDecoration:"underline",textUnderlineOffset:"3px"}}>Audit readiness</Link> assesses the SOC 2 control set against the policies you upload — a readiness check, not an audit.</>,
                <>There is no regulator feed and no statutory filing calendar on this product.</>,
              ].map((fact,i) => (
                <li key={i} style={{display:"flex",gap:"10px",fontSize:"13.5px",lineHeight:1.65,color:"rgba(19,18,16,.72)"}}>
                  <span style={{color:"var(--cu)",flexShrink:0}}>✓</span><span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* What the privacy programme covers */}
        <section style={{borderBottom:"1px solid var(--b200)"}}>
          <div style={{padding: "44px clamp(20px,4vw,52px)",borderBottom:"1px solid var(--b200)"}}>
            <div className="ey rev">Privacy &amp; Data Protection</div>
            <h2 className="sh2 rev d1">One engine.{" "}<br /><em>Fourteen laws.</em></h2>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)"}}>
            {[
              {name:"Know what applies",desc:"Applicability wizard, obligation register, readiness calendar and gap assessment for the laws you hold.",count:"Applicability"},
              {name:"Know your data",desc:"System and personal-data inventory, flows and lineage, AI data discovery over files you upload, processing and purpose registers.",count:"Inventory"},
              {name:"Answer people",desc:"Notices with versions, consent and withdrawals, a public request portal, and each law's own response period on every request.",count:"Rights"},
              {name:"Respond and prove",desc:"Breach response with each law's notification clocks, retention and deletion, processor DPA review, DPIAs, transfers and audit packs.",count:"Evidence"},
            ].map((r,i) => (
              <div key={i} className="rev" style={{padding:"40px 38px",borderRight:i<3?"1px solid var(--b200)":"none",transition:"background .2s"}} onMouseEnter={e=>(e.currentTarget.style.background="var(--b100)")} onMouseLeave={e=>(e.currentTarget.style.background="")}>
                <div style={{fontFamily:"var(--font-fraunces), serif",fontSize:"22px",fontWeight:200,color:"var(--o900)",marginBottom:"8px"}}>{r.name}</div>
                <div style={{fontSize:"13px",color:"rgba(19,18,16,.52)",lineHeight:1.7,marginBottom:"12px"}}>{r.desc}</div>
                <div style={{fontSize:"11px",color:"var(--cu)"}}>{r.count}</div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section style={{padding: "44px clamp(20px,4vw,52px)",borderBottom:"1px solid var(--b200)",background:"var(--b100)"}}>
          <div className="ey rev">How it works</div>
          <h2 className="sh2 rev d1" style={{marginBottom:"32px"}}>Decide. Record. <em>Evidence.</em></h2>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"24px"}}>
            {[
              {n:"01",title:"Decide what applies",desc:"Your country picks the starting law; the applicability wizard and obligation register follow from the laws you hold."},
              {n:"02",title:"Build the records",desc:"Inventory, processing activities, notices, consent, processors and transfers — each a register your organisation owns."},
              {n:"03",title:"Run and evidence it",desc:"Work requests and incidents against each law's clocks, delete on schedule, and export audit packs with the evidence linked."},
            ].map((s,i) => (
              <div key={i} className="rev" style={{padding:"32px",background:"var(--b50)",border:"1px solid var(--b200)",borderRadius:"6px"}}>
                <div style={{fontFamily:"var(--font-fraunces), serif",fontSize:"11px",color:"var(--cu)",marginBottom:"12px"}}>{s.n}</div>
                <div style={{fontFamily:"var(--font-fraunces), serif",fontSize:"19px",fontWeight:200,color:"var(--o900)",marginBottom:"8px"}}>{s.title}</div>
                <div style={{fontSize:"13px",color:"rgba(19,18,16,.52)",lineHeight:1.7}}>{s.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{padding:"72px clamp(20px,4vw,52px)",textAlign:"center",background:"var(--o950)"}}>
          <h2 style={{fontFamily:"var(--font-fraunces), serif",fontSize:"clamp(28px,3.5vw,42px)",fontWeight:200,color:"var(--b50)",letterSpacing:"-.03em",marginBottom:"14px"}}>A programme you can{" "}<br /><em style={{color:"var(--cl)",fontStyle:"italic"}}>show your work on.</em></h2>
          <p style={{fontSize:"15px",color:"rgba(251,250,246,.35)",marginBottom:"28px"}}>The privacy programme is on Pro, Business and Enterprise.</p>
          <a className="cta-p" data-mag="true" href={`/sign-up`}><span className="cta-p-t">Get started →</span></a>
        </section>

        <FaqSection faqs={PRODUCT_FAQS} />
      </PageShell>
    </>
  );
}
